import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useDoc } from '../hooks/useFirestore';
import { Item } from '../types/item';
import { Collection } from '../types/collection';
import {
  Typography,
  Box,
  Paper,
  IconButton,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function ItemDetailLayout() {
  const { cid, iid } = useParams();
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch the item data
  const {
    snapshot: item,
    isLoading: isLoadingItem,
    error: itemError,
  } = useDoc<Item>(`users/${userId}/collections/${cid}/items/${iid}`);

  // Fetch the collection data for breadcrumb context
  const {
    snapshot: collection,
    isLoading: isLoadingCollection,
  } = useDoc<Collection>(`users/${userId}/collections/${cid}`);

  // Handle item deletion
  const handleDeleteItem = async () => {
    if (!cid || !iid) return;
    
    try {
      setIsDeleting(true);
      await deleteDoc(doc(db, `users/${userId}/collections/${cid}/items/${iid}`));
      // Navigate back to the collection after deletion
      navigate(`/h/collection/${cid}`);
    } catch (error) {
      console.error("Error deleting item:", error);
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (isLoadingItem || isLoadingCollection) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (itemError) {
    return (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          bgcolor: 'error.light', 
          color: 'error.dark',
          borderRadius: 2,
          mt: 2
        }}
      >
        <Typography>Error: {itemError.message}</Typography>
      </Paper>
    );
  }

  if (!item) {
    return (
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          textAlign: 'center', 
          borderRadius: 2,
          bgcolor: 'action.hover',
          mt: 2
        }}
      >
        <Typography variant="body1">
          Item not found. It may have been deleted or the URL is incorrect.
        </Typography>
        <Button 
          variant="contained" 
          sx={{ mt: 2 }} 
          onClick={() => navigate(`/h/collection/${cid}`)}
        >
          Return to Collection
        </Button>
      </Paper>
    );
  }

  // Format dates
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate();
    return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString();
  };

  return (
    <Box>
      {/* Breadcrumb and actions */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: { xs: 2, md: 3 },
          px: { xs: 0.5, sm: 0 },
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <IconButton 
            onClick={() => navigate(`/h/collection/${cid}`)} 
            sx={{ mr: 1 }}
            aria-label="Back to collection"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              flexWrap: 'wrap',
              gap: 0.5
            }}
          >
            {collection?.name || 'Collection'} /
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              ml: 1, 
              fontWeight: 500,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              wordBreak: 'break-word'
            }}
          >
            {item.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton 
            color="primary" 
            onClick={() => navigate(`/h/collection/${cid}/item/${iid}/edit`)}
            aria-label="Edit item"
          >
            <EditIcon />
          </IconButton>
          <IconButton 
            color="error" 
            onClick={() => setDeleteDialogOpen(true)}
            aria-label="Delete item"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Item details */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ px: { xs: 0.5, sm: 0 } }}>
        {/* Image section */}
        <Grid item xs={12} md={5}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: { xs: 2, md: 3 },
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.02)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {item.imageUrl ? (
              <Box 
                component="img"
                src={item.imageUrl}
                alt={item.name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: { xs: 250, md: 350 },
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            ) : (
              <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No image available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Details section */}
        <Grid item xs={12} md={7}>
          <Paper 
            elevation={1} 
            sx={{ 
              p: { xs: 2, md: 3 },
              borderRadius: 2,
            }}
          >
            <Typography 
              variant="h5" 
              gutterBottom
              sx={{ 
                fontWeight: 500,
                display: { xs: 'none', md: 'block' }
              }}
            >
              {item.name}
            </Typography>

            <Divider sx={{ mb: 2, display: { xs: 'none', md: 'block' } }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Description
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  whiteSpace: 'pre-wrap',
                  mb: 2
                }}
              >
                {item.description || 'No description provided.'}
              </Typography>
            </Box>

            {/* Tags Section */}
            {item.tags && item.tags.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {item.tags.map((tag, index) => (
                      <Chip 
                        key={index} 
                        label={tag} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Metadata */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Item Details
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Chip
                  icon={<CalendarIcon />}
                  label={`Created: ${formatDate(item.createdAt)}`}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  icon={<CalendarIcon />}
                  label={`Updated: ${formatDate(item.updatedAt)}`}
                  variant="outlined"
                  size="small"
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{item.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteItem} 
            color="error" 
            disabled={isDeleting}
            variant="contained"
          >
            {isDeleting ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}